import { useState } from "react";
import { X, SplitSquareHorizontal } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, formatMoney, formatDate } from "@/lib/api";

export function SplitBillModal({ bill, payPeriods, onClose, onSplit }) {
  // Default: half the current amount, land on the first day of the NEXT pay period.
  const currentAmount = Number(bill.estimated_due_amount || 0);
  const [splitAmount, setSplitAmount] = useState(() => (currentAmount / 2).toFixed(2));
  // Suggest the next pay period's period_start as the default new due date.
  const suggestedDate = (() => {
    const today = new Date().toISOString().slice(0, 10);
    const future = (payPeriods || [])
      .filter((p) => p.period_start > today)
      .sort((a, b) => a.period_start.localeCompare(b.period_start));
    if (future.length > 0) return future[0].period_start;
    // Fallback: 14 days after the bill's current due date
    const d = new Date(bill.due_date + "T00:00:00");
    d.setDate(d.getDate() + 14);
    return d.toISOString().slice(0, 10);
  })();
  const [splitDate, setSplitDate] = useState(suggestedDate);
  const [saving, setSaving] = useState(false);

  const amtNum = Number(splitAmount);
  const invalid = !amtNum || amtNum <= 0 || amtNum >= currentAmount || !splitDate;

  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/bills/${bill.id}/split`, {
        split_amount: amtNum, split_date: splitDate,
      });
      onSplit(data.original, data.split);
      toast.success(`Split "${bill.name}" — ${formatMoney(amtNum)} moved to ${formatDate(splitDate)}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
      data-testid="split-modal-backdrop"
    >
      <div
        className="w-full max-w-md rounded-2xl bg-white shadow-2xl p-6"
        onClick={(e) => e.stopPropagation()}
        data-testid="split-modal"
      >
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="flex items-center gap-2 text-[#4A6148]">
              <SplitSquareHorizontal size={18} />
              <div className="text-xs font-semibold uppercase tracking-widest">Split into a payment plan</div>
            </div>
            <h2 className="font-heading text-xl text-[#2C302E] mt-1">{bill.name}</h2>
            <p className="text-xs text-[#59605C] mt-1 leading-relaxed">
              Peel a portion of this bill (currently <span className="font-semibold">{formatMoney(currentAmount)}</span>) into a second bill that lands on a later date. The original bill is reduced by the same amount so nothing double-counts.
            </p>
          </div>
          <button className="text-[#8A918D] hover:text-[#2C302E] shrink-0" onClick={onClose} aria-label="Close" data-testid="split-close">
            <X size={20} />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-widest text-[#59605C]">Amount to peel</span>
            <input
              type="number"
              min="0.01"
              step="0.01"
              max={currentAmount - 0.01}
              value={splitAmount}
              onChange={(e) => setSplitAmount(e.target.value)}
              className="mt-1 w-full px-3 py-2 rounded-xl border border-[#CDC7BB] bg-white text-[#2C302E] focus:outline-none focus:border-[#4A6148]"
              data-testid="split-amount-input"
            />
          </label>
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-widest text-[#59605C]">New due date</span>
            <input
              type="date"
              value={splitDate}
              onChange={(e) => setSplitDate(e.target.value)}
              className="mt-1 w-full px-3 py-2 rounded-xl border border-[#CDC7BB] bg-white text-[#2C302E] focus:outline-none focus:border-[#4A6148]"
              data-testid="split-date-input"
            />
          </label>
        </div>

        <div className="mt-3 text-xs text-[#59605C]">
          After: original becomes <span className="font-semibold text-[#2C302E]">{formatMoney(Math.max(0, currentAmount - (amtNum || 0)))}</span>, new bill created for <span className="font-semibold text-[#2C302E]">{formatMoney(amtNum || 0)}</span>.
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button className="btn-ghost !px-3 !py-2 text-sm" onClick={onClose} disabled={saving} data-testid="split-cancel">Cancel</button>
          <button className="btn-primary text-sm" onClick={submit} disabled={saving || invalid} data-testid="split-save">
            {saving ? "Splitting…" : "Split bill"}
          </button>
        </div>
      </div>
    </div>
  );
}
